import React from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { TextField, Typography, Box, Alert, useTheme, IconButton } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { Plus, X } from "lucide-react";
import RecordingControls from "@/modules/common/components/audio/RecordingControls";
import RecordButton from "@/modules/common/components/audio/RecordButton";
import RecordingEntryDisplay from "@/modules/common/components/audio/RecordingEntryDisplay";

interface CommentWithRecordingInputProps {
  recording: any;
  control: Control<any>;
  errors: FieldErrors<any>;
  commentState: {
    showAddCommentSection: boolean;
    setShowAddCommentSection: (show: boolean) => void;
    addCommentText: string;
    setAddCommentText: (text: string) => void;
  };
  computed: {
    isTypingInInitialComment: boolean | undefined | string;
    isTypingInAddComment: boolean;
  };
}

const CommentWithRecordingInput: React.FC<CommentWithRecordingInputProps> = ({
  recording,
  control,
  errors,
  commentState: {
    showAddCommentSection,
    setShowAddCommentSection,
    addCommentText,
    setAddCommentText,
  },
  computed: { isTypingInInitialComment, isTypingInAddComment },
}) => {
  const theme: any = useTheme();

  return (
    <Grid item mobile={12}>
      {/* Permission Error Alert */}
      {recording.permissionError && (
        <Alert 
          severity="warning" 
          sx={{ mb: 1.5 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={recording.clearPermissionError}
            >
              <X size={18} />
            </IconButton>
          }
        >
          {recording.permissionError}
        </Alert>
      )}

      {/* Existing recordings */}
      {recording.recordings.map((rec: any) => (
          <RecordingEntryDisplay
            key={rec.id}
            recording={rec}
            isPlaying={recording.isPlaying(rec.id)}
            currentPlaybackTime={recording.getCurrentTimeForId(rec.id)}
            onRemove={recording.removeRecording}
            onTogglePlay={() => recording.togglePlayback(rec)}
            theme={theme}
            error={recording.getErrorForId(rec.id)}
            onSeek={(time) => recording.seekTo(rec.id, time)}
            playbackRate={recording.playbackRate}
            onPlaybackRateChange={recording.setPlaybackRate}
          />
        ))}

        {/* Recording UI */}
        {recording.isRecording && (
          <RecordingControls
            isPaused={recording.isPaused}
            recordingTime={recording.recordingTime}
            audioLevels={recording.audioLevels}
            onCancel={recording.cancelRecording}
            onPauseResume={recording.pauseResume}
            onConfirm={recording.confirmRecording}
            theme={theme}
          />
        )}

        {/* Initial comment section */}
        {recording.recordings.length === 0 && !recording.isRecording && (
          <>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.tertiary,
                mb: 0.5,
              }}
            >
              Comment
            </Typography>
            <Box
              sx={{
                position: "relative",
                border: "1px solid",
                borderColor: errors.comment
                  ? theme.nawy.colors.semantic.border.extension.negative
                  : theme.nawy.colors.semantic.border.primary,
                borderRadius: 2,
                minHeight: 120,
                "&:focus-within": {
                  borderColor: errors.comment
                    ? theme.nawy.colors.semantic.border.extension.negative
                    : theme.nawy.colors.semantic.border.accent,
                },
              }}
            >
              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Comment"
                    multiline
                    rows={4}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      "& .MuiInputBase-root": {
                        padding: 1.5,
                        paddingBottom: 5,
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                        ...theme.nawy.typography.english.variants.body.md
                          .regular,
                      },
                    }}
                  />
                )}
              />
              {!isTypingInInitialComment && (
                <Box sx={{ position: "absolute", bottom: 12, right: 12 }}>
                  <RecordButton
                    onClick={recording.startRecording}
                    isInitializing={recording.isInitializing}
                    theme={theme}
                  />
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Add comment link */}
        {recording.recordings.length > 0 &&
          !recording.isRecording &&
          !showAddCommentSection &&
          recording.canAddMoreRecordings && (
            <Box
              onClick={() => setShowAddCommentSection(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 1.5,
                cursor: "pointer",
                color: theme.nawy.colors.semantic.text.accent,
                "&:hover": { opacity: 0.8 },
              }}
            >
              <Plus size={16} />
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.semibold,
                }}
              >
                Add comment
              </Typography>
            </Box>
          )}

        {/* Add comment section */}
        {recording.recordings.length > 0 &&
          !recording.isRecording &&
          showAddCommentSection && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.nawy.colors.semantic.text.tertiary,
                  mb: 0.5,
                }}
              >
                Comment
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  border: "1px solid",
                  borderColor: theme.nawy.colors.semantic.border.primary,
                  borderRadius: 2,
                  minHeight: 120,
                  "&:focus-within": {
                    borderColor: theme.nawy.colors.semantic.border.accent,
                  },
                }}
              >
                <TextField
                  value={addCommentText}
                  onChange={(e) => setAddCommentText(e.target.value)}
                  fullWidth
                  placeholder="Comment"
                  multiline
                  rows={4}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInputBase-root": {
                      padding: 1.5,
                      paddingBottom: 5,
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                      ...theme.nawy.typography.english.variants.body.md
                        .regular,
                    },
                  }}
                />
                {recording.canAddMoreRecordings && !isTypingInAddComment && (
                  <Box sx={{ position: "absolute", bottom: 12, right: 12 }}>
                    <RecordButton
                      onClick={recording.startRecording}
                      isInitializing={recording.isInitializing}
                      theme={theme}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}

        {/* Validation error */}
        {errors.comment && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errors.comment.message as string}
          </Alert>
        )}
      </Grid>
  );
};

export default CommentWithRecordingInput;

